import { Typography, useTheme } from '@mui/material';
import { type ExposureOrGroup, useSubmission } from 'api/submission';
import { DRIVER_VALIDATION_MESSAGE } from 'common/constants';
import { ExposuresList, ExposuresListItem, ExposuresWrapper } from 'components/ExposuresWrapper';
import { displayToastMessage } from 'components/ToastMessage';
import {
  getDriverValidationMaxVehicleCount,
  parseConfigVersion,
  shouldSkipDriversStep,
} from 'helpers/utils';
import { useExposuresOrGroupFields } from 'hooks/useExposuresOrGroupFields';
import { type FC, useRef } from 'react';
import { useParams } from 'react-router-dom';
import type { IDriver } from './Drives.types';
import { DriverModal } from './components/Modal';
import type { DriverModalRef } from './components/Modal/DriverModal.types';

const category = 'driver';

const title = (data: IDriver) => `${data.pol_drivers_first} ${data.pol_drivers_last}`;

const Drivers: FC = () => {
  const themeHook = useTheme();
  const { id: submissionId } = useParams() as { id: string };
  const { data: submission } = useSubmission(submissionId);

  const { relatedResource, performAction } = useExposuresOrGroupFields<IDriver>({
    type: 'group',
    name: 'pol_drivers',
  });

  const driverModalRef = useRef<DriverModalRef>(undefined);

  const handleAdd = async (data: IDriver) =>
    performAction({
      action: 'add',
      data,
      loadingText: `Adding ${title(data)}`,
    });

  const handleUpdate = async (data: IDriver, id: string) =>
    performAction({
      action: 'update',
      data,
      resourceId: id,
      loadingText: `Updating the ${title(data)}`,
    });

  const handleDelete = async (driver: ExposureOrGroup<IDriver>) =>
    performAction({
      action: 'delete',
      resourceId: driver.id,
      loadingText: `Deleting the ${title(driver.data)}`,
    });

  const handleGoNext = async () => {
    // Check if driver validation applies and if there are no drivers
    if (!shouldSkipDriversStep(submission) && relatedResource.length === 0) {
      const configVersion = parseConfigVersion(submission?.three_config_version);
      const maxVehicleCount = getDriverValidationMaxVehicleCount(configVersion);
      displayToastMessage('ERROR', DRIVER_VALIDATION_MESSAGE(maxVehicleCount));
      return false;
    }

    return true;
  };

  return (
    <>
      <DriverModal
        ref={driverModalRef}
        handleAdd={handleAdd}
        handleUpdate={handleUpdate}
        category={category}
      />

      <ExposuresWrapper
        title="Add your drivers"
        description="Tell us about your drivers, include owners, employees or any subcontractors that will be driving the vehicles you own, lease or add to this policy."
        addButtonText={`Add ${category}`}
        onAddBtnClick={() => driverModalRef.current?.openModal()}
        onNext={handleGoNext}
      >
        <ExposuresList>
          {relatedResource.map((driver) => (
            <ExposuresListItem
              key={driver.id}
              category={category}
              title={title(driver.data)}
              onEdit={() => driverModalRef.current?.openModal(driver)}
              onDelete={() => {
                handleDelete(driver);
              }}
            >
              <Typography sx={themeHook.mixins.textOverflowStyle()}>
                {driver.data.pol_drivers_license_number}
              </Typography>
            </ExposuresListItem>
          ))}
        </ExposuresList>
      </ExposuresWrapper>
    </>
  );
};

export default Drivers;
